import React, { useEffect, useId } from 'react'

import clsx from 'clsx'

import ApplyV2SelectSingle from '@core/components/ApplyDialogV2/ApplyV2SelectSingle'
import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'

import './ApplyV2DateSelectGroup.scss'

interface Props {
    label?: string
    topBar?: React.ReactNode
    names?: {
        day?: string
        month?: string
        year?: string
    }
    value: {
        day?: string | number | null
        month?: string | number | null
        year?: string | number | null
    }
    errors?: {
        day?: string | null | boolean
        month?: string | null | boolean
        year?: string | null | boolean
    }
    noExpiryChecked?: boolean
    onChange?: (date: {
        day?: string | number | null
        month?: string | number | null
        year?: string | number | null
    }) => void
    isDisabled?: boolean
    className?: string
    onFocus?: React.FocusEventHandler<HTMLDivElement>
    onBlur?: React.FocusEventHandler<HTMLDivElement>
    id?: string | number
    name?: string
    // errorMessage?: string | null | boolean
    isRequired?: boolean
    isShowErrorMessage?: boolean
    // isShowExpirationHeader?: boolean
    isLoading?: boolean
    isFutureYears?: boolean
    yearsRange?: number
    yearsSart?: number | string | null
    yearsEnd?: number | string | null
    description?: React.ReactNode
    isHideInputs?: boolean
    isCurrentYear?: boolean
    isClearable?: boolean
}

const baseClass = 'ApplyV2DateSelectGroup'

const currentYear = new Date().getFullYear()

const days = Array.from({ length: 31 }, (_, i) => ({
    value: (i + 1).toString().padStart(2, '0'),
    label: (i + 1).toString().padStart(2, '0'),
}))

const getYearsOptions = (
    yearsRange: number,
    isFutureYears: boolean,
    isCurrentYear: boolean,
    yearsSart?: number | string | null,
    yearsEnd?: number | string | null
) => {
    // const currentYear = new Date().getFullYear();
    const minEndYear = isCurrentYear ? currentYear : currentYear + 100
    let startYear: number

    if (yearsSart === null || yearsSart === undefined) {
        startYear = currentYear
    } else if (typeof yearsSart === 'string') {
        const parsed = parseInt(yearsSart, 10)
        startYear = isNaN(parsed) ? currentYear : parsed
    } else {
        startYear = yearsSart
    }

    // return Array.from({ length: yearsRange }, (_, i) => ({
    //     value: (isFutureYears ? startYear + i : startYear - i).toString(),
    //     label: (isFutureYears ? startYear + i : startYear - i).toString(),
    // }))

    const initialEndYear = isFutureYears ? startYear + yearsRange - 1 : startYear
    // const initialEndYear = Math.max(isFutureYears ? startYear + yearsRange - 1 : startYear, minEndYear)

    const oldestYear = isFutureYears ? initialEndYear : startYear - yearsRange + 1

    // console.log(
    //     'yearsRange -',
    //     yearsRange,
    //     'initialEndYear - ',
    //     initialEndYear,
    //     'isFutureYears - ',
    //     isFutureYears,
    //     'isCurrentYear - ',
    //     isCurrentYear,
    //     'yearsSart - ',
    //     yearsSart,
    //     'startYear - ',
    //     startYear,
    //     'yearsEnd - ',
    //     yearsEnd,
    //     'oldestYear-',
    //     oldestYear,
    //     'minEndYear - ',
    //     minEndYear
    // )

    if (startYear && yearsEnd) {
        let endYear: number
        if (typeof yearsEnd === 'string') {
            const parsed = parseInt(yearsEnd, 10)
            endYear = isNaN(parsed) ? currentYear : parsed
        } else {
            endYear = yearsEnd
        }
        if (endYear > startYear) {
            return Array.from({ length: endYear - startYear + 1 }, (_, i) => {
                const year = startYear + i
                return {
                    value: year.toString(),
                    label: year.toString(),
                }
            })
        } else {
            return Array.from({ length: startYear - endYear + 1 }, (_, i) => {
                const year = endYear + i
                return {
                    value: year.toString(),
                    label: year.toString(),
                }
            })
        }
    }

    if (oldestYear < minEndYear) {
        const yearsToAdd = minEndYear - oldestYear
        return Array.from({ length: yearsToAdd }, (_, i) => {
            const year = isFutureYears ? startYear + i : minEndYear - i
            return {
                value: year.toString(),
                label: year.toString(),
            }
        })
    }

    return Array.from({ length: yearsRange }, (_, i) => {
        const year = isFutureYears ? startYear + i : minEndYear - i
        return {
            value: year.toString(),
            label: year.toString(),
        }
    })
}

function ApplyV2DateSelectGroup({
    label,
    value = { day: null, month: null, year: null },
    noExpiryChecked = false,
    onFocus,
    onBlur,
    onChange = () => {},
    isDisabled = false,
    className = '',
    id,
    name,
    isRequired = false,
    isShowErrorMessage = true,
    // isShowExpirationHeader = false,
    isLoading = false,
    names,
    errors,
    isFutureYears = true,
    yearsRange = 40,
    yearsSart = currentYear,
    description,
    topBar,
    isCurrentYear = false,
    isClearable = false,
    yearsEnd,
}: Props) {
    const defaultId = useId()
    const groupId = id || defaultId
    const { getTitle } = useApplyV2NavTranslations()
    const [currentValue, setCurrentValue] = React.useState(value)
    const [isShow, setIsShow] = React.useState(true)

    const errorMessage = [...new Set([errors?.day, errors?.month, errors?.year].filter(Boolean))].join(', ')

    const months = [
        { value: '01', label: getTitle('477', 'January') },
        { value: '02', label: getTitle('478', 'February') },
        { value: '03', label: getTitle('479', 'March') },
        { value: '04', label: getTitle('480', 'April') },
        { value: '05', label: getTitle('481', 'May') },
        { value: '06', label: getTitle('482', 'June') },
        { value: '07', label: getTitle('483', 'July') },
        { value: '08', label: getTitle('484', 'August') },
        { value: '09', label: getTitle('485', 'September') },
        { value: '10', label: getTitle('486', 'October') },
        { value: '11', label: getTitle('487', 'November') },
        { value: '12', label: getTitle('488', 'December') },
    ]

    // const years = Array.from({ length: yearsRange }, (_, i) => ({
    //     value: (isFutureYears ? currentYear + i : currentYear - i).toString(),
    //     label: (isFutureYears ? currentYear + i : currentYear - i).toString(),
    // }))

    const years = getYearsOptions(yearsRange, isFutureYears, isCurrentYear, yearsSart, yearsEnd)

    useEffect(() => {
        setCurrentValue(value)
    }, [value])

    useEffect(() => {
        setIsShow(false)
        const timer = setTimeout(() => {
            setIsShow(true)
        }, 0)
        return () => {
            clearTimeout(timer)
        }
    }, [isDisabled])

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--error`]: !!errorMessage,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
            })}
            onFocus={onFocus}
            tabIndex={-1}
            data-error={!!errorMessage ? 'true' : 'false'}
        >
            {label && (
                <div
                    className={clsx(`${baseClass}__header`, {
                        [`${className}__header`]: !!className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__header--title`, {
                            [`${className}__header--title`]: !!className,
                        })}
                    >
                        {decodeHtmlEntitiesRecursive(label)}
                        {isRequired && '*'}
                    </div>
                    {topBar}
                </div>
            )}

            {isShow && (
                <div className={`${baseClass}__date-fields`}>
                    <ApplyV2SelectSingle
                        id={`${groupId}-day`}
                        inputMode={'numeric'}
                        value={currentValue.day}
                        name={names?.day || `day-${name}`}
                        options={days}
                        placeholder={getTitle('10524', 'DD')}
                        isLoading={isLoading}
                        className={clsx(`${baseClass}__select`, {
                            [`${className}__select`]: !!className,
                        })}
                        errorMessage={errors?.day}
                        isShowErrorMessage={false}
                        onChange={(v) => {
                            onChange({
                                day: v,
                                month: currentValue ? currentValue.month : null,
                                year: currentValue ? currentValue.year : null,
                            })
                        }}
                        onBlur={onBlur}
                        isDisabled={isDisabled}
                        onInputChange={(input) => {
                            return input.replace(/\D/g, '').slice(0, 2)
                        }}
                        isClearable={isClearable}
                        noSearchResultsLabel={getTitle('6253', 'No results')}
                    />

                    <ApplyV2SelectSingle
                        id={`${groupId}-month`}
                        value={currentValue.month}
                        name={names?.month || `month-${name}`}
                        options={months}
                        // placeholder="Month"
                        placeholder={getTitle('931', 'Month')}
                        isLoading={isLoading}
                        className={clsx(`${baseClass}__select`, `${baseClass}__select-month`, {
                            [`${className}__select`]: !!className,
                            [`${className}__select-month`]: !!className,
                        })}
                        errorMessage={errors?.month}
                        isShowErrorMessage={false}
                        onChange={(v) => {
                            onChange({
                                day: currentValue ? currentValue.day : null,
                                month: v,
                                year: currentValue ? currentValue.year : null,
                            })
                        }}
                        onBlur={onBlur}
                        isDisabled={isDisabled}
                        onInputChange={(input) => {
                            return input.slice(0, 20)
                        }}
                        isClearable={isClearable}
                        noSearchResultsLabel={getTitle('6253', 'No results')}
                    />

                    <ApplyV2SelectSingle
                        id={`${groupId}-year`}
                        inputMode={'numeric'}
                        value={currentValue.year}
                        name={names?.year || `year-${name}`}
                        options={years}
                        placeholder={getTitle('10525', 'YYYY')}
                        isLoading={isLoading}
                        className={clsx(`${baseClass}__select`, {
                            [`${className}__select`]: !!className,
                        })}
                        errorMessage={errors?.year}
                        isShowErrorMessage={false}
                        onChange={(v) => {
                            onChange({
                                day: currentValue ? currentValue.day : null,
                                month: currentValue ? currentValue.month : null,
                                year: v,
                            })
                        }}
                        onBlur={onBlur}
                        isDisabled={isDisabled}
                        onInputChange={(input) => {
                            return input.replace(/\D/g, '').slice(0, 4)
                        }}
                        isClearable={isClearable}
                        noSearchResultsLabel={getTitle('6253', 'No results')}
                    />
                </div>
            )}
            {!isShow && (
                <div className={`${baseClass}__date-fields`}>
                    <ApplyV2SelectSingle
                        options={[]}
                        placeholder="DD"
                        className={clsx(`${baseClass}__select`, {
                            [`${className}__select`]: !!className,
                        })}
                    />

                    <ApplyV2SelectSingle
                        options={[]}
                        placeholder={getTitle('931', 'Month')}
                        className={clsx(`${baseClass}__select`, `${baseClass}__select-month`, {
                            [`${className}__select`]: !!className,
                            [`${className}__select-month`]: !!className,
                        })}
                    />

                    <ApplyV2SelectSingle
                        options={[]}
                        placeholder="YYYY"
                        className={clsx(`${baseClass}__select`, {
                            [`${className}__select`]: !!className,
                        })}
                    />
                </div>
            )}

            {description && (
                <div
                    className={clsx(`${baseClass}__description`, {
                        [`${className}__description`]: className,
                    })}
                >
                    {description}
                </div>
            )}
            {errorMessage && isShowErrorMessage && (
                <div className={`${baseClass}__error`}>
                    <div className={`${baseClass}__error--icon`}>
                        <WarningErrorIcon />
                    </div>
                    <div className={`${baseClass}__error--text`}>{errorMessage}</div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2DateSelectGroup
